import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  SlidersHorizontal,
  X,
  RotateCcw,
  ChevronDown,
  Tag,
  ArrowUpDown,
  Coins,
} from 'lucide-react';
import clsx from 'clsx';
import { useFilterStore } from '../../stores/filterStore';
import { useCategories } from '../../services/productService';
import { getCategoryIcon } from '../../lib/categoryIcons';

const SORT_OPTIONS = [
  { value: 'popular', label: 'Popular' },
  { value: 'price_asc', label: 'Price: Low → High' },
  { value: 'price_desc', label: 'Price: High → Low' },
  { value: 'newest', label: 'Newest First' },
];

/**
 * Filter controls shared between desktop sidebar and mobile slide-over.
 */
function FilterControls({ onApply }) {
  const {
    search, categoryId, sortBy, minPrice, maxPrice,
    setSearch, setCategoryId, setSortBy, setMinPrice, setMaxPrice,
    resetFilters,
  } = useFilterStore();

  const { data: categories } = useCategories();
  const hasActiveFilters = search || categoryId || sortBy !== 'popular' || minPrice || maxPrice;

  const activeFilterCount = [
    search,
    categoryId,
    sortBy !== 'popular' && sortBy,
    minPrice,
    maxPrice,
  ].filter(Boolean).length;

  return (
    <div className="space-y-6">
      {/* Search */}
      <div>
        <label className="flex items-center gap-1.5 text-xs font-bold text-brand-dark uppercase tracking-wide mb-2">
          <Search size={12} />
          Search
        </label>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Product name..."
            className="w-full pl-9 pr-3 py-2.5 bg-brand-light/50 border border-ui-border rounded-lg text-sm text-brand-dark placeholder:text-ui-muted focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red/20 transition-all"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-brand-light"
            >
              <X size={12} className="text-brand-gray" />
            </button>
          )}
        </div>
      </div>

      {/* Category */}
      <div>
        <label className="flex items-center gap-1.5 text-xs font-bold text-brand-dark uppercase tracking-wide mb-2">
          <Tag size={12} />
          Category
        </label>
        <div className="space-y-0.5">
          <button
            onClick={() => setCategoryId('')}
            className={clsx(
              'flex items-center gap-2 w-full text-left px-3 py-2 rounded-lg text-sm transition-all',
              !categoryId
                ? 'bg-brand-red text-brand-white font-semibold shadow-sm'
                : 'text-brand-dark hover:bg-brand-light'
            )}
          >
            <span className={clsx(
              'w-1.5 h-1.5 rounded-full flex-shrink-0',
              !categoryId ? 'bg-brand-white' : 'bg-brand-gray/30'
            )} />
            All Categories
          </button>
          {categories?.map((cat) => {
            const CatIcon = getCategoryIcon(cat.icon);
            return (
            <button
              key={cat.id}
              onClick={() => setCategoryId(cat.id)}
              className={clsx(
                'flex items-center gap-2 w-full text-left px-3 py-2 rounded-lg text-sm transition-all',
                categoryId === cat.id
                  ? 'bg-brand-red text-brand-white font-semibold shadow-sm'
                  : 'text-brand-dark hover:bg-brand-light'
              )}
            >
              <CatIcon size={14} className="flex-shrink-0" />
              {cat.name}
            </button>
            );
          })}
        </div>
      </div>

      {/* Price range */}
      <div>
        <label className="flex items-center gap-1.5 text-xs font-bold text-brand-dark uppercase tracking-wide mb-2">
          <Coins size={12} />
          Price Range (KES)
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={minPrice}
            onChange={(e) => setMinPrice(e.target.value)}
            placeholder="Min"
            min="0"
            className="flex-1 min-w-0 px-3 py-2.5 bg-brand-light/50 border border-ui-border rounded-lg text-sm text-brand-dark placeholder:text-ui-muted focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red/20 transition-all"
          />
          <span className="text-ui-muted text-xs font-medium">—</span>
          <input
            type="number"
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
            placeholder="Max"
            min="0"
            className="flex-1 min-w-0 px-3 py-2.5 bg-brand-light/50 border border-ui-border rounded-lg text-sm text-brand-dark placeholder:text-ui-muted focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red/20 transition-all"
          />
        </div>
      </div>

      {/* Sort */}
      <div>
        <label className="flex items-center gap-1.5 text-xs font-bold text-brand-dark uppercase tracking-wide mb-2">
          <ArrowUpDown size={12} />
          Sort By
        </label>
        <div className="space-y-0.5">
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setSortBy(opt.value)}
              className={clsx(
                'flex items-center gap-2 w-full text-left px-3 py-2 rounded-lg text-sm transition-all',
                sortBy === opt.value
                  ? 'bg-brand-red text-brand-white font-semibold shadow-sm'
                  : 'text-brand-dark hover:bg-brand-light'
              )}
            >
              <span className={clsx(
                'w-1.5 h-1.5 rounded-full flex-shrink-0',
                sortBy === opt.value ? 'bg-brand-white' : 'bg-brand-gray/30'
              )} />
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Reset */}
      {hasActiveFilters && (
        <button
          onClick={() => {
            resetFilters();
            onApply?.();
          }}
          className="flex items-center gap-1.5 w-full justify-center py-2.5 text-sm font-semibold text-brand-red border border-brand-red/20 rounded-lg hover:bg-brand-red/5 transition-colors"
        >
          <RotateCcw size={13} />
          Reset all filters
          {activeFilterCount > 0 && (
            <span className="ml-1 w-5 h-5 flex items-center justify-center text-[10px] font-bold bg-brand-red text-brand-white rounded-full">
              {activeFilterCount}
            </span>
          )}
        </button>
      )}
    </div>
  );
}

/**
 * Desktop sticky sidebar — rendered directly in page layout.
 */
export function DesktopFilterSidebar() {
  return (
    <aside className="hidden lg:block w-60 xl:w-64 flex-shrink-0 self-start">
      <div className="sticky top-20 bg-brand-white rounded-2xl border border-ui-border shadow-sm overflow-hidden">
        <div className="p-4 border-b border-ui-border">
          <h3 className="flex items-center gap-2 text-sm font-bold text-brand-dark">
            <SlidersHorizontal size={14} className="text-brand-red" />
            Filters
          </h3>
        </div>
        <div className="p-4">
          <FilterControls />
        </div>
      </div>
    </aside>
  );
}

/**
 * Mobile filter trigger bar — compact search + filter button.
 */
export function MobileFilterBar() {
  const { search, categoryId, sortBy, minPrice, maxPrice, setSearch, setMobileFiltersOpen } = useFilterStore();
  const hasActiveFilters = search || categoryId || sortBy !== 'popular' || minPrice || maxPrice;

  const activeFilterCount = [
    categoryId,
    sortBy !== 'popular' && sortBy,
    minPrice,
    maxPrice,
  ].filter(Boolean).length;

  return (
    <div className="lg:hidden sticky top-14 sm:top-16 z-20 bg-brand-white border-b border-ui-border py-2.5 px-4 shadow-sm">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products..."
            className="w-full pl-9 pr-3 py-2.5 bg-brand-light/50 border border-ui-border rounded-lg text-sm placeholder:text-ui-muted focus:outline-none focus:border-brand-red transition-colors"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-brand-light"
            >
              <X size={12} className="text-brand-gray" />
            </button>
          )}
        </div>
        <button
          onClick={() => setMobileFiltersOpen(true)}
          className={clsx(
            'relative flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-semibold border transition-colors',
            hasActiveFilters
              ? 'bg-brand-red text-brand-white border-brand-red'
              : 'bg-brand-white text-brand-dark border-ui-border hover:bg-brand-light'
          )}
        >
          <SlidersHorizontal size={14} />
          <span className="hidden sm:inline">Filters</span>
          {activeFilterCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 flex items-center justify-center text-[9px] font-bold bg-brand-white text-brand-red border border-brand-red rounded-full">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>
    </div>
  );
}

/**
 * Mobile filter slide-over panel.
 */
export function MobileFilterPanel() {
  const { mobileFiltersOpen, setMobileFiltersOpen } = useFilterStore();

  return (
    <AnimatePresence>
      {mobileFiltersOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMobileFiltersOpen(false)}
            className="fixed inset-0 bg-brand-dark/50 z-40 lg:hidden"
          />
          {/* Panel */}
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed top-0 left-0 bottom-0 w-full max-w-[300px] bg-brand-white z-50 lg:hidden shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3.5 border-b border-ui-border flex-shrink-0">
              <h3 className="flex items-center gap-2 text-base font-bold text-brand-dark">
                <SlidersHorizontal size={16} className="text-brand-red" />
                Filters
              </h3>
              <button
                onClick={() => setMobileFiltersOpen(false)}
                className="p-1.5 rounded-lg text-brand-dark hover:bg-brand-light transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto overscroll-contain p-4">
              <FilterControls onApply={() => setMobileFiltersOpen(false)} />
            </div>

            {/* Sticky footer */}
            <div className="p-4 border-t border-ui-border bg-brand-white flex-shrink-0">
              <button
                onClick={() => setMobileFiltersOpen(false)}
                className="w-full py-3 bg-brand-red text-brand-white font-bold rounded-xl hover:bg-red-700 transition-colors text-sm"
              >
                Show Results
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
